import { useMemo, useState } from 'react';
import { type ConfirmMixturesSelection, useGetMixtureConcentration } from '@practium/features/api';
import { useConfirmModal, useToast } from '@practium/ui';
import { formatPeriodLabel } from '../constants/maintenance.constants';
import type { FertilizationPlanAdvice, MaintenanceMixture } from '../types';
import {
	areMixturesConfirmed,
	fromBackendUsage,
	getMixtureMonths,
	getMonthMixtures,
	highestUsage,
	mapMixtureMonthSummary,
	mapMixtureNutrientDemand,
	mapMixtureSystemRecommendation,
	toEmptyUsage,
} from '../utils/mixturesMapper';
import { useMixtureActions } from './useMixtureActions';
import { useMixtureComposer } from './useMixtureComposer';
import { useMixtureListFilter } from './useMixtureListFilter';
import { useMixtureProductCatalog } from './useMixtureProductCatalog';
import { useMixtureSettingsForm } from './useMixtureSettingsForm';

interface UseMixturesPanelOptions {
	advice: FertilizationPlanAdvice;
	onSaved: () => void;
}

const isPending = (mixture: MaintenanceMixture) => mixture.status === 'pending';

export const useMixturesPanel = ({ advice, onSaved }: UseMixturesPanelOptions) => {
	const toast = useToast();
	const pendingModal = useConfirmModal();
	const mixtures = advice.mixtures ?? [];
	const savedSettings = advice.fractionationSettings;
	const { catalog, productOptions, comboProductOptions } = useMixtureProductCatalog(advice);
	const settings = useMixtureSettingsForm(savedSettings);
	const actions = useMixtureActions(advice);
	const composer = useMixtureComposer();
	const [isView, setIsView] = useState(() => areMixturesConfirmed(mixtures));
	const [selectedMonth, setSelectedMonth] = useState('');

	const hasMixtures = mixtures.length > 0;
	const hasConfirmed = mixtures.some((mixture) => mixture.status === 'confirmed');
	const canGenerate = !hasMixtures || (settings.isChanged && !hasConfirmed);
	const showSectorName = (advice.sectors ?? []).length > 1;

	const months = useMemo(() => getMixtureMonths(advice), [advice]);
	const activeMonth = months.includes(selectedMonth) ? selectedMonth : (months[0] ?? '');
	const monthMixtures = useMemo(
		() => getMonthMixtures(mixtures, activeMonth),
		[activeMonth, mixtures]
	);
	const list = useMixtureListFilter({
		mixtures: monthMixtures,
		showSector: showSectorName,
		month: activeMonth,
	});
	const positions = useMemo(
		() => new Map(monthMixtures.map((mixture, index) => [mixture._id, index + 1])),
		[monthMixtures]
	);
	const positionOf = (mixture: MaintenanceMixture) => positions.get(mixture._id) ?? 0;
	const pendingIds = monthMixtures.filter(isPending).map((mixture) => mixture._id);
	const confirmedCount = monthMixtures.length - pendingIds.length;
	const reviewMixtures = list.isFiltered ? list.filteredMixtures : monthMixtures;
	const reviewPendingIds = reviewMixtures.filter(isPending).map((mixture) => mixture._id);
	const pendingByMonth = useMemo(
		() =>
			months
				.map((month) => ({
					month,
					count: getMonthMixtures(mixtures, month).filter(isPending).length,
				}))
				.filter((item) => item.count > 0),
		[mixtures, months]
	);

	const summary = useMemo(() => mapMixtureMonthSummary(advice, activeMonth), [activeMonth, advice]);
	const nutrientDemand = useMemo(
		() => mapMixtureNutrientDemand(advice, activeMonth),
		[activeMonth, advice]
	);
	const systemRecommendation = useMemo(
		() => mapMixtureSystemRecommendation(advice, activeMonth, catalog),
		[activeMonth, advice, catalog]
	);

	const { data: concentrations } = useGetMixtureConcentration(
		advice._id ?? '',
		composer.products,
		!!savedSettings
	);
	const emptyUsage = useMemo(
		() =>
			toEmptyUsage(
				mixtures.find((mixture) => mixture.usage)?.usage?.massLimitKg ?? 0,
				savedSettings?.cemMax ?? 0
			),
		[mixtures, savedSettings?.cemMax]
	);
	const composerUsage =
		composer.products.length && concentrations?.length
			? fromBackendUsage(highestUsage(concentrations))
			: emptyUsage;
	const usageOf = (mixture: MaintenanceMixture) =>
		mixture.usage ? fromBackendUsage(mixture.usage) : emptyUsage;

	const run = async (action: () => Promise<void>, successMessage?: string) => {
		try {
			await action();
			if (successMessage) toast.success(successMessage);
			return true;
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Não foi possível concluir a ação.');
			return false;
		}
	};

	const handleGenerate = async () => {
		const validSettings = settings.validate();
		if (!validSettings) return;
		composer.reset();
		await run(() => actions.generate(validSettings), 'Misturas elaboradas com sucesso.');
	};

	const handleSubmitComposer = async () => {
		const draft = composer.buildDraft();
		if (!draft) return;
		const editingId = composer.editingId;
		const saved = await run(
			() => (editingId ? actions.update(editingId, draft) : actions.create(draft)),
			editingId ? 'Mistura atualizada.' : 'Mistura inserida.'
		);
		if (saved) composer.reset();
	};

	const confirmSelection = async (selection: ConfirmMixturesSelection, mixtureIds: string[]) => {
		const confirmed = await run(() => actions.confirm(selection));
		if (confirmed && composer.editingId && mixtureIds.includes(composer.editingId)) {
			composer.reset();
		}
	};

	const handleConfirm = (mixtureId: string) =>
		confirmSelection({ mixtureIds: [mixtureId] }, [mixtureId]);

	const handleConfirmAll = () =>
		confirmSelection({ period: activeMonth, ...list.confirmFilter }, reviewPendingIds);

	const handleSave = async () => {
		if (!pendingByMonth.length) {
			setIsView(true);
			onSaved();
			return;
		}

		const total = pendingByMonth.reduce((sum, item) => sum + item.count, 0);
		const goToPending = await pendingModal.confirm({
			title: 'Misturas pendentes',
			message: `${total} ${total === 1 ? 'mistura ainda precisa' : 'misturas ainda precisam'} ser confirmadas antes de salvar: ${pendingByMonth
				.map((item) => `${formatPeriodLabel(item.month)} (${item.count})`)
				.join(', ')}.`,
			confirmText: 'Ir para o mês pendente',
			cancelText: 'Fechar',
			variant: 'warning',
		});
		if (goToPending) setSelectedMonth(pendingByMonth[0].month);
	};

	return {
		isView,
		hasMixtures,
		isPending: actions.isPending,
		settingsForm: settings.form,
		settingsErrors: settings.errors,
		savedSettings,
		setSetting: settings.set,
		canGenerate,
		handleGenerate,
		months,
		activeMonth,
		setSelectedMonth,
		summary,
		nutrientDemand,
		systemRecommendation,
		monthMixtures,
		list,
		positionOf,
		confirmedCount,
		reviewMixtures,
		reviewPendingIds,
		usageOf,
		showSectorName,
		composer,
		composerUsage,
		productOptions,
		comboProductOptions,
		handleSubmitComposer,
		handleConfirm,
		handleConfirmAll,
		pendingModal,
		handleSave,
	};
};
